import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import * as LDClient from "launchdarkly-js-client-sdk";

import _ from "lodash";
import { useBackupUserFlags, User, useUserFlagsBackup } from "~/hooks/useUser";
import { useAuth } from "~/state/auth";
import { useEnv } from "~/state/env";
import { KebabCase } from "~/etc/helpers";
const LD_KEY = "launchDarkly";

interface Props {
    children: ReactNode;
}

export interface LDFlags {
    hubspotIntegration?: boolean;
    enableEditor?: boolean;
    enableDatasetEditor?: boolean;
    enableQuickLinkEditor?: boolean;
    enableDemoScriptEditor?: boolean;
    enableNewDemoExperienceV1?: boolean;
    enableCustomerSubdomains?: boolean;
    enableSandboxTutorial?: boolean;
    enableIntuitDemoFix?: boolean;
    hideDemoPanelFooter?: boolean;
    hideSandboxPanelFooter?: boolean;
    enableDemoControlTotp?: boolean;
    preventLeadsFetching?: boolean;
    sendIntuitToDemoRoute?: boolean;
    openDemoControlsInNewWindow?: boolean;
    enablePresenterMode?: boolean;
    preventOldAppFrameInNewSandboxExperience?: boolean;
    demoScriptFeatureShortcuts?: boolean;
    fixDoubleIframing?: boolean;
    showChromeExtensionAlertMessage?: boolean;
    enableSalespersonEmailNotifications?: boolean;
    enableActivityTracking?: boolean;
    useServerSideMixpanelTracking?: boolean;
    enableNoframing?: boolean;
    enableSellerSandboxLink?: boolean;
    enableUserSandboxQueryParameter?: boolean;
    enablePartnerManagement?: boolean;
    enableAlwaysShowNewUserMedia?: boolean;
    enableAccuratePurchasedLicensesCount?: boolean;
    enableArchiveDeals?: boolean;
    usePreAuthTropicBrevoModal?: boolean;
    enableReadOnlyPartnerDeals?: boolean;
    enableNoframingSandboxes?: boolean;
    enableDatasetGenerator?: boolean;
    enableDatasetGeneratorV2?: boolean;
    enableQuickLinksInPresenterMode?: boolean;
    enablePresenterModeTimer?: boolean;
    enableNoShortcutBanner?: boolean;
    enableRemoveStakeholderAction?: boolean;
    enableBulkInvite?: boolean;
    enableBulkResendActivation?: boolean;
    dealsDashboardV4Ui?: boolean;
    enableG2ReviewButton?: boolean;
    enableUserpilot?: boolean;
    /**
     * (0 - 100)% of the users will be tracked
     */
    datadogRumSampleRate?: number;
    /**
     * (0 - 100)% of the users will have their session replayed
     * relative to the percentage of users that are tracked
     */
    datadogRumSessionReplaySampleRate?: number;
    /**
     * This flag is useless when NF sbx is fully rolled out, as well as the /sandbox_preview route
     */
    enableSandboxPreviewRoute?: boolean;
    /**
     * Enables the new self-signup flow (/signup, self_signup landing redirect).
     * Disabled by default until rolled out.
     */
    enableSelfSignup?: boolean;
    /**
     * When enabled, self-signup users are auto-routed into a requirements
     * agent chat on first visit instead of the static welcome page.
     */
    enableSelfSignupDirectToChat?: boolean;
    enableProductFilter?: boolean;
    enableBuyerAgent?: boolean;
    enableDealApprovalFlow?: boolean;
    /**
     * Gates the "Send me a Magic Link" option on the unauthenticated
     * login page (password-step). Global on/off rollout — not user
     * targeted; the LD context resolved on the backend is anonymous.
     */
    enableLoginMagicLink?: boolean;
    /**
     * When enabled, sandbox URLs use workspace_id instead of trial_id as the
     * canonical identifier.  Legacy trial-based URLs are redirected.
     */
    enableSandboxRouteByWorkspaceId?: boolean;
    /**
     * Gates the new routing behaviour: unmatched routes render a dedicated
     * 404 page (instead of the legacy `AppFrame` shell or a never-resolving
     * spinner), and authenticated content is served through frameless
     * renderers. Acts as the kill-switch for the 404 page + route cleanup.
     */
    enable404PageAndRouteCleanup?: boolean;
    /**
     * Allows the same buyer (matched by email) to be a stakeholder on multiple
     * concurrent deals within the same seller company. Suppresses the
     * duplicate-stakeholder modal and pins same-product deals via
     * `?workspace_id=` in the sandbox selector / PartnerWatcher.
     */
    enableMultiDealBuyers?: boolean;
    /**
     * Integer grace window, in days, during which an already-expired sandbox
     * can still be extended/edited from the Edit Deal & Change Expiration
     * flows. Counted from the trial's `expires_at`; while
     * `now <= expires_at + N days` the deal stays editable, afterwards it is
     * read-only.
     */
    expiredSandboxExtensionGraceDays?: number;
}

/** This is what LaunchDarkly returns */
type KebabCaseLDFlags = {
    [K in keyof LDFlags as K extends string ? KebabCase<K> : never]: LDFlags[K];
};

export const EDIT_FLAGS_MANUALLY =
    import.meta.env.VITE_MANUAL_LD_FLAGS === "true";

export const IS_DEV = process.env.NODE_ENV === "development";

export function createDefaultsFlags(defaults?: Partial<LDFlags>): LDFlags {
    return {
        hubspotIntegration: false,
        enableEditor: false,
        enableDatasetEditor: false,
        enableQuickLinkEditor: false,
        enableDemoScriptEditor: false,
        enableSandboxTutorial: false,
        enableIntuitDemoFix: false,
        hideDemoPanelFooter: false,
        hideSandboxPanelFooter: false,
        enableDemoControlTotp: false,
        preventLeadsFetching: false,
        sendIntuitToDemoRoute: false,
        openDemoControlsInNewWindow: false,
        enablePresenterMode: false,
        preventOldAppFrameInNewSandboxExperience: IS_DEV,
        demoScriptFeatureShortcuts: false,
        fixDoubleIframing: false,
        showChromeExtensionAlertMessage: false,
        enableSalespersonEmailNotifications: false,
        enableActivityTracking: false,
        useServerSideMixpanelTracking: false,
        enableNoframing: false,
        enableSellerSandboxLink: true,
        enablePartnerManagement: false,
        enableAccuratePurchasedLicensesCount: false,
        enableArchiveDeals: false,
        usePreAuthTropicBrevoModal: false,
        enableReadOnlyPartnerDeals: false,
        enableNoframingSandboxes: false,
        enableAlwaysShowNewUserMedia: false,
        enableDatasetGenerator: false,
        enableDatasetGeneratorV2: false,
        enableQuickLinksInPresenterMode: false,
        enablePresenterModeTimer: false,
        enableNoShortcutBanner: false,
        enableRemoveStakeholderAction: false,
        enableBulkInvite: false,
        enableBulkResendActivation: false,
        dealsDashboardV4Ui: false,
        enableG2ReviewButton: false,
        enableUserpilot: false,
        datadogRumSampleRate: 0,
        datadogRumSessionReplaySampleRate: 0,
        enableSandboxPreviewRoute: false,
        enableSelfSignup: false,
        enableSelfSignupDirectToChat: false,
        enableProductFilter: false,
        enableBuyerAgent: false,
        enableDealApprovalFlow: false,
        enableLoginMagicLink: false,
        enableSandboxRouteByWorkspaceId: IS_DEV,
        enable404PageAndRouteCleanup: IS_DEV,
        enableMultiDealBuyers: false,
        expiredSandboxExtensionGraceDays: 90,

        // Up for cleanup flags:
        enableNewDemoExperienceV1: true,
        enableCustomerSubdomains: true,
        enableUserSandboxQueryParameter: true,
        ...defaults,
    };
}

export const LaunchDarklyContext = createContext<{
    flags: LDFlags;
    ready: boolean;
    setFlags: (flags: LDFlags) => void;
}>({ flags: {}, ready: false, setFlags: () => {} });

export function useLaunchDarkly() {
    return useContext(LaunchDarklyContext);
}

export function useFlags() {
    const { flags, ready, setFlags } = useLaunchDarkly();
    return { flags, ready, setFlags };
}

const tryJSONParse = <T,>(json?: string | null): T | undefined => {
    if (json) {
        try {
            return JSON.parse(json);
        } catch (e) {
            return undefined;
        }
    }
};

interface LDLocalStorageRecord {
    flags: LDFlags;
    testMode: boolean;
}
const getLocalRecord = (key: string): LDLocalStorageRecord | undefined => {
    const record = localStorage.getItem(key);
    return tryJSONParse(record);
};

function getEnableEditor(flags: LDFlags) {
    return _.some([
        flags.enableQuickLinkEditor,
        flags.enableDemoScriptEditor,
        flags.enableDatasetEditor,
        flags.enableBuyerAgent,
    ]);
}

function getEnableIntuitDemoFix(originalFlags: LDFlags) {
    // We want sendIntuitToDemoRoute to supersede enableIntuitDemoFix.
    // Guarantee that when the new flag is true, the old one is false.
    if (originalFlags.sendIntuitToDemoRoute) {
        return false;
    }
    // sendIntuitToDemoRoute is false, so return whatever this was.
    return originalFlags.enableIntuitDemoFix;
}

const getPseudoFlags = (flags: LDFlags) => {
    return {
        enableEditor: getEnableEditor(flags),
        enableIntuitDemoFix: getEnableIntuitDemoFix(flags),
    };
};

function getLaunchDarklyUserContext(user?: User) {
    const isPartner =
        user?.current_workspace?.deal_salesperson_company_name !==
        user?.current_workspace?.company_name;
    return {
        kind: "user",
        key: user?.user_id || "not-logged-in",
        email: user?.email,
        // NOTE: may need to change lines below to user?.extra_info?.{first,last}Name
        firstName: user?.first_name,
        lastName: user?.last_name,
        anonymous: !user?.user_id,
        vendor: user?.current_workspace?.deal_salesperson_company_name,
        partneredCompany: isPartner
            ? user?.current_workspace?.company_name
            : null,
        salesperson_email: user?.current_workspace?.deal_salesperson_email,
    };
}

interface FlagsData {
    flags: LDFlags;
    ready: boolean;
}

export function LaunchDarklyContextProvider({ children }: Props) {
    const { data: user, loading: isUserLoading } = useAuth();
    const [flagsData, setFlagsData] = useState<FlagsData>({
        ready: false,
        flags: {},
    });

    const ldClient = useRef<LDClient.LDClient>();

    const env = useEnv();
    const clientSideID = env?.LAUNCH_DARKLY_CLIENT_ID || "";

    const { refetch: fetchUserFlags } = useUserFlagsBackup({
        enabled: false,
    });
    const { mutate: backupFlags } = useBackupUserFlags();

    const isLocal =
        window.location.hostname.endsWith("local.testbox.com") ||
        window.location.hostname.endsWith("tbx-local.toybox.company") ||
        // Brevo can't be iframed
        window.location.hostname.startsWith("testbox-local.brevo.com") ||
        window.location.hostname.startsWith("testbox-local.tropicapp.io") ||
        window.location.hostname.startsWith("local.toybox.company"); // For testing Toybox NF sandbox

    const toCamelCase = (flags: LDFlags | KebabCaseLDFlags) => {
        return _.mapKeys(flags, (v, k) => _.camelCase(k));
    };

    const changeFlags = (
        flags: LDFlags | KebabCaseLDFlags,
        testMode = false
    ) => {
        const camelFlags = toCamelCase(flags);
        localStorage.setItem(
            LD_KEY,
            JSON.stringify({ flags: camelFlags, testMode })
        );
        const ldFlagsWithPseudoFlags = {
            ready: true,
            flags: {
                ...camelFlags,
                ...getPseudoFlags(camelFlags),
            },
        };
        setFlagsData(ldFlagsWithPseudoFlags);
        return { camelFlags, allFlags: ldFlagsWithPseudoFlags };
    };

    const backupFlagsIfNecessary = async (flags: LDFlags) => {
        const today = new Date();

        const lastBackedUpAt = user?.last_backed_up_at
            ? new Date(user.last_backed_up_at)
            : undefined;

        const isBackupStale =
            lastBackedUpAt?.toDateString() !== today.toDateString();

        if (isBackupStale) {
            const defaultFlags = createDefaultsFlags();
            const nonDefaultFlags = _.pickBy(
                flags,
                (value, key) =>
                    !_.isEqual(value, defaultFlags[key]) && key in defaultFlags
            );
            backupFlags(nonDefaultFlags);
        }
    };

    const onLaunchDarklyFlagsReady = () => {
        const ldFlags = ldClient.current?.allFlags() as KebabCaseLDFlags;
        const { camelFlags } = changeFlags(
            createDefaultsFlags(toCamelCase(ldFlags))
        );
        if (user && ldFlags && !_.isEmpty(ldFlags)) {
            backupFlagsIfNecessary(camelFlags);
        }
    };

    const onLaunchDarklyError = async (...args: any[]) => {
        console.log("LaunchDarkly error detected! ", args);
        const response = await fetchUserFlags();
        changeFlags(createDefaultsFlags(response.data));
    };

    /*
     * TODO (EXP-973) - make the pattern we use to get a fake `enableEditor` flag more generalized
     */
    useEffect(() => {
        if (isUserLoading) {
            return;
        }

        const ldMockedStorageTestFlags = getLocalRecord(LD_KEY);

        const localStorageFlags = ldMockedStorageTestFlags;

        if (isLocal && localStorageFlags?.testMode) {
            changeFlags(createDefaultsFlags(localStorageFlags.flags), true);
        } else if (clientSideID) {
            const context = getLaunchDarklyUserContext(user);
            ldClient.current = LDClient.initialize(clientSideID, context);
            ldClient.current.on("ready", onLaunchDarklyFlagsReady);
            ldClient.current.on("error", onLaunchDarklyError);
        } else if (isLocal) {
            changeFlags(createDefaultsFlags(localStorageFlags?.flags));
        } else {
            if (env) {
                console.error(
                    "Critical error - LaunchDarkly clientId is missing!"
                );
            }
            changeFlags({});
        }
    }, [clientSideID, user?.user_id, isUserLoading, isLocal]);

    return (
        <LaunchDarklyContext.Provider
            value={{
                flags: flagsData.flags || {},
                ready: flagsData.ready,
                setFlags: changeFlags,
            }}
        >
            <div
                data-testid="launch-darkly"
                data-test-flags-ready={flagsData.ready}
                style={{ display: "none" }}
            />
            {children}
        </LaunchDarklyContext.Provider>
    );
}
